import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, LabeledIconInput, Row, SharedStyleTypes, Toggle, ToggleLink, Typography } from '@commonsku/styles';
import { AlertBanner, AlertBannerContent, AlertBannerDescription, AlertBannerIndicator } from '@commonsku/design-system';
import { useCommonskuPaymentsCreditCardFeeCap, useIdentity } from '../../../hooks';
import config from '../../../config';
import CREDIT_CARD_PROVIDERS from '../../../constants/CreditCardProviders';
import { getCreditCardFeeCap } from '../../../constants/CreditCardFeeCaps';

export interface AdminPaymentCreditCardFeeProps {
    data: any,
    save: any,
}

export const AdminPaymentCreditCardFee = ({ ...props }: AdminPaymentCreditCardFeeProps & SharedStyleTypes) => {
    const identity = useIdentity();
    const shouldEnforceCreditCardFeeCap = useCommonskuPaymentsCreditCardFeeCap(props.data);
    const defaultValues = useMemo(() => ({
        payment_cc_fee_enabled: 0,
        payment_cc_fee_percentage: config.payment_cc_fee_percentage,
        payment_cc_fee_name: 'Credit Card Fee',
    }), []);

    const defaultErrors = useMemo(() => ({
        payment_cc_fee_percentage: '',
    }), []);

    const [form, setForm] = useState(defaultValues);
    const [formErrors, setFormErrors] = useState(defaultErrors);

    useEffect(() => {
        let values = defaultValues;
        values = {
            payment_cc_fee_enabled: props.data.payment_cc_fee_enabled ?? 0,
            payment_cc_fee_percentage: props.data.payment_cc_fee_percentage ?? config.payment_cc_fee_percentage,
            payment_cc_fee_name: props.data.payment_cc_fee_name ?? 'Credit Card Fee',
        };
        setForm(values);
    }, [props.data.payment_cc_fee_enabled, props.data.payment_cc_fee_percentage, defaultValues, props.data.payment_cc_fee_name]);

    const setFormFieldValue = useCallback((e) => {
        const target = e.target;
        setForm((form) => ({ ...form, [target.name]: target.value }));
    }, []);

    const validateForm = useCallback((data) => {
        setFormErrors(defaultErrors);
        let errorCount = 0;
        // Check data and set form errors
        for (const field of ['payment_cc_fee_percentage']) {
            if (data[field] === '') {
                setFormErrors((errors) => ({ ...errors, [field]: 'Required' }));
                errorCount++;
            }
        }

        if (isNaN(data['payment_cc_fee_percentage']) || data['payment_cc_fee_percentage'] < 0) {
            setFormErrors((errors) => ({ ...errors, payment_cc_fee_percentage: 'Invalid number' }));
            errorCount++;
        }

        const feeCap = getCreditCardFeeCap();
        if (shouldEnforceCreditCardFeeCap && data['payment_cc_fee_percentage'] > feeCap) {
            setFormErrors((errors) => ({ ...errors, payment_cc_fee_percentage: `Fee must be ${feeCap}% or lower for compliance reasons.` }));
            errorCount++;
        }

        if (errorCount > 0) {
            return false;
        }

        return true;
    }, [defaultErrors, shouldEnforceCreditCardFeeCap]);

    // Only clover connect, stripe with cc enabled, or commonsku payments with cc enabled, we should show this
    if (!(
        (props.data.credit_card_software === CREDIT_CARD_PROVIDERS.STRIPE.value && props.data.stripe_payment_cc) ||
        props.data.credit_card_software === CREDIT_CARD_PROVIDERS.CARDCONNECT.value ||
        (props.data.credit_card_software === CREDIT_CARD_PROVIDERS.COMMONSKU_PAYMENTS.value && props.data.forward_payment_cc))
    ) {
        return null;
    }

    const getTextInput = (field, label = null) => {
        const hasError = !!formErrors[field];
        return (
            <LabeledIconInput Icon={<div>%</div>} label={label || field} name={field} value={form[field]} onChange={setFormFieldValue} error={hasError} noMargin onBlur={(e) => {
                if (!validateForm(form)) {
                    return;
                }
                props.save(form);
            }} />
        );
    };

    const feePercentageError = formErrors['payment_cc_fee_percentage'];

    const formFields = (
        <>
            <Row align="flex-start">
                {+form.payment_cc_fee_enabled === 0 ? null : <Col sm={6}>
                    <Row align="flex-start">
                        <Col padded sm={8}>
                            {getTextInput('payment_cc_fee_name', 'Fee Name')}
                        </Col>
                        <Col padded sm={4}>
                            {getTextInput('payment_cc_fee_percentage', 'Default Fee')}
                        </Col>
                        {!feePercentageError ? null :
                            <Col padded sm={12} style={{ paddingTop: 0 }}>
                                <AlertBanner status="error" variant="subtle">
                                    <AlertBannerIndicator />
                                    <AlertBannerContent>
                                        <AlertBannerDescription>{feePercentageError}</AlertBannerDescription>
                                    </AlertBannerContent>
                                </AlertBanner>
                            </Col>}
                    </Row>
                </Col>}
                <Col padded sm={6}>
                    <Typography style={{ fontWeight: 'bold' }}>Who pays the fee by default? (you can override this)</Typography>
                    <Toggle size="medium">
                        <ToggleLink onClick={() => {
                            setFormFieldValue({
                                target: {
                                    name: 'payment_cc_fee_enabled',
                                    value: 0,
                                }
                            });
                            props.save({
                                'payment_cc_fee_enabled': 0,
                            });
                        }} selected={+form.payment_cc_fee_enabled === 0}>{identity.company_name}</ToggleLink>
                        <ToggleLink onClick={() => {
                            setFormFieldValue({
                                target: {
                                    name: 'payment_cc_fee_enabled',
                                    value: 1,
                                }
                            });
                            props.save({
                                'payment_cc_fee_enabled': 1,
                            });
                        }} selected={+form.payment_cc_fee_enabled === 1}>The Client</ToggleLink>
                    </Toggle>
                </Col>
                {+form.payment_cc_fee_enabled === 0 ? null :
                    <Col padded sm={12}>
                        A line item for the credit card fee will be automatically added to the invoice.
                    </Col>}
            </Row>
        </>
    );
    return formFields;
};
